/**
 * Composable for managing audio waveform data
 */

import { ref, type Ref, type ComputedRef, computed } from 'vue'
import type { WaveformData } from '../types'
import { parseWaveformData, generateWaveformPeaks } from '../utils/audio-utils'

export interface UseAudioWaveformOptions {
  onLoad?: (waveform: WaveformData) => void
  onError?: (error: Error) => void
}

export interface UseAudioWaveformReturn {
  // State
  waveformData: Ref<WaveformData | undefined>
  isLoading: Ref<boolean>
  error: Ref<Error | undefined>

  // Computed
  hasPeaks: ComputedRef<boolean>
  peakCount: ComputedRef<number>

  // Methods
  loadWaveform: (url: string) => Promise<void>
  generateFromAudio: (audioBuffer: AudioBuffer, numBars?: number) => void
  setWaveformData: (data: WaveformData) => void
  clear: () => void
}

/**
 * Composable for loading and managing waveform data
 */
export function useAudioWaveform(options: UseAudioWaveformOptions = {}): UseAudioWaveformReturn {
  const { onLoad, onError } = options

  // State
  const waveformData = ref<WaveformData | undefined>()
  const isLoading = ref(false)
  const error = ref<Error | undefined>()

  // Abort controller for cancelling requests
  let abortController: AbortController | null = null

  // Computed
  const hasPeaks = computed(() => {
    return waveformData.value !== undefined && waveformData.value.peaks.length > 0
  })

  const peakCount = computed(() => {
    return waveformData.value?.peaks.length ?? 0
  })

  /**
   * Loads waveform data from a URL
   */
  async function loadWaveform(url: string): Promise<void> {
    // Cancel any previous request
    if (abortController) {
      abortController.abort()
    }

    abortController = new AbortController()
    isLoading.value = true
    error.value = undefined

    try {
      const response = await fetch(url, {
        signal: abortController.signal,
      })

      if (!response.ok) {
        throw new Error(`Failed to load waveform: ${response.status} ${response.statusText}`)
      }

      const buffer = await response.arrayBuffer()
      const data = parseWaveformData(buffer)

      waveformData.value = data
      onLoad?.(data)
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Request was cancelled, ignore
        return
      }

      const loadError = err instanceof Error ? err : new Error('Failed to load waveform')
      error.value = loadError
      onError?.(loadError)
    } finally {
      isLoading.value = false
      abortController = null
    }
  }

  /**
   * Generates waveform data from an AudioBuffer
   */
  function generateFromAudio(audioBuffer: AudioBuffer, numBars: number = 100): void {
    try {
      const peaks = generateWaveformPeaks(audioBuffer, numBars)
      const data: WaveformData = {
        peaks,
        sampleRate: audioBuffer.sampleRate,
        duration: audioBuffer.duration,
      }

      waveformData.value = data
      onLoad?.(data)
    } catch (err) {
      const genError = err instanceof Error ? err : new Error('Failed to generate waveform')
      error.value = genError
      onError?.(genError)
    }
  }

  /**
   * Directly sets waveform data
   */
  function setWaveformData(data: WaveformData): void {
    waveformData.value = data
    onLoad?.(data)
  }

  /**
   * Clears waveform data and resets state
   */
  function clear(): void {
    if (abortController) {
      abortController.abort()
      abortController = null
    }

    waveformData.value = undefined
    isLoading.value = false
    error.value = undefined
  }

  return {
    // State
    waveformData,
    isLoading,
    error,

    // Computed
    hasPeaks,
    peakCount,

    // Methods
    loadWaveform,
    generateFromAudio,
    setWaveformData,
    clear,
  }
}
